import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';

export default function TypesOfDSCRLoansPage() {
  const loanTypes = [
    {
      title: 'Single Family Rental DSCR Loans',
      icon: 'ri-home-4-line',
      description: 'Perfect for traditional long-term rental properties',
      features: ['Up to 80% LTV', 'DSCR as low as 1.0', 'Rates from 6.99%', 'No limit on properties'],
      bestFor: 'Investors building traditional rental portfolios with stable, long-term tenants'
    },
    {
      title: 'Short-Term Rental DSCR Loans',
      icon: 'ri-calendar-check-line',
      description: 'Specialized financing for Airbnb and vacation rentals',
      features: ['Up to 75% LTV', 'DSCR from 1.0', 'Rates from 7.50%', 'Uses projected STR income'],
      bestFor: 'Investors in tourist markets leveraging higher short-term rental income'
    },
    {
      title: 'Multifamily DSCR Loans',
      icon: 'ri-building-line',
      description: 'Finance 2-4 unit or 5+ unit apartment buildings',
      features: ['Up to 80% LTV', 'DSCR from 1.20', 'Rates from 7.00%', 'Portfolio financing available'],
      bestFor: 'Investors scaling with duplexes, triplexes, or larger apartment complexes'
    },
    {
      title: 'No Ratio DSCR Loans',
      icon: 'ri-shield-check-line',
      description: 'Flexible financing with DSCR below 1.0',
      features: ['Up to 75% LTV', 'DSCR as low as 0.75', 'Rates from 8.00%', 'Higher down payment'],
      bestFor: 'Value-add properties or renovations where income will improve'
    },
    {
      title: 'Cash-Out Refinance DSCR Loans',
      icon: 'ri-money-dollar-circle-line',
      description: 'Access equity from your rental properties',
      features: ['Up to 75% LTV', 'DSCR from 1.25', 'Rates from 7.25%', 'No income verification'],
      bestFor: 'Investors extracting equity to fund additional property purchases'
    },
    {
      title: 'Fix and Flip DSCR Loans',
      icon: 'ri-hammer-line',
      description: 'Short-term financing for property renovations',
      features: ['Up to 90% of purchase + rehab', 'Interest-only payments', '12-24 month terms', 'Fast approval'],
      bestFor: 'Active flippers needing quick capital for renovation projects'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <StickyQuoteBar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Types of <span className="text-emerald-400">DSCR Loans</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Explore different DSCR loan programs designed for various investment strategies. From traditional rentals to short-term vacation properties, find the right financing solution.
            </p>
            <Link
              to="/apply"
              className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-amber-500/30 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Find Your Loan Type
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Loan Types Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loanTypes.map((loan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-emerald-500"
              >
                <div className="bg-blue-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${loan.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{loan.title}</h3>
                <p className="text-slate-600 mb-4">{loan.description}</p>
                
                <div className="space-y-2 mb-4">
                  {loan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center text-sm">
                      <i className="ri-check-line text-blue-600 mr-2"></i>
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-white rounded-lg p-3 mt-4">
                  <p className="text-xs text-slate-600 font-semibold mb-1">Best For:</p>
                  <p className="text-sm text-slate-700">{loan.bestFor}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Understanding Different Types of DSCR Loans
            </h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              DSCR loans come in various types designed to meet the diverse needs of real estate investors. Whether you're investing in traditional long-term rentals, short-term vacation properties, or multifamily apartment buildings, there's a DSCR loan type tailored to your investment strategy. Understanding the different types of DSCR loans helps you choose the right financing option for your specific property and goals.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Single Family Rental DSCR Loans
            </h3>
            
            <p className="text-slate-700 mb-4">
              Single family rental DSCR loans are the most common type of DSCR financing, designed specifically for traditional rental properties with long-term tenants. These loans allow investors to purchase or refinance single-family homes, townhouses, and condos without providing personal income documentation.
            </p>

            <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <h4 className="font-bold text-slate-900 mb-4">Key Features of Single Family DSCR Loans:</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-blue-600 text-xl mr-2"></i>
                  <span><strong>Loan-to-Value up to 80%:</strong> Finance up to 80% of the property value with just 20% down</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-blue-600 text-xl mr-2"></i>
                  <span><strong>DSCR as low as 1.0:</strong> Qualify with rental income that covers your mortgage payment</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-blue-600 text-xl mr-2"></i>
                  <span><strong>Competitive rates:</strong> Starting at 6.99% for well-qualified borrowers</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-blue-600 text-xl mr-2"></i>
                  <span><strong>No property limits:</strong> Finance unlimited rental properties in your portfolio</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Short-Term Rental DSCR Loans
            </h3>
            
            <p className="text-slate-700 mb-4">
              Short-term rental DSCR loans are specialized financing products for Airbnb, VRBO, and vacation rental properties. These types of DSCR loans recognize the higher income potential of short-term rentals and use projected rental income based on market data and comparable properties.
            </p>

            <p className="text-slate-700 mb-6">
              Unlike traditional rental loans, short-term rental DSCR loans calculate income using an appraisal with a short-term rental analysis or actual booking history. This makes them ideal for investors in tourist destinations, ski resorts, beach towns, and other vacation markets where nightly rates significantly exceed traditional monthly rents.
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-8 border-l-4 border-blue-600">
              <h4 className="font-bold text-slate-900 mb-3">Short-Term Rental DSCR Loan Requirements:</h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                  <span>DSCR ratio of 1.0 or higher based on projected STR income</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                  <span>Property must be in an area that allows short-term rentals</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                  <span>Appraisal with short-term rental income analysis required</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-blue-600 mr-2 mt-1"></i>
                  <span>Credit score typically 680+ for best rates</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Multifamily DSCR Loans
            </h3>
            
            <p className="text-slate-700 mb-4">
              Multifamily DSCR loans finance properties with 2-4 units (small multifamily) or 5+ units (commercial multifamily). These types of DSCR loans are popular among investors looking to scale their portfolios efficiently, as multifamily properties generate income from multiple units while requiring management of just one property.
            </p>

            <p className="text-slate-700 mb-6">
              For 2-4 unit properties, multifamily DSCR loans function similarly to single-family loans but may require slightly higher DSCR ratios (typically 1.20+). For properties with 5 or more units, lenders often offer portfolio financing options that allow investors to finance multiple properties under a single loan.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-slate-900 mb-3">2-4 Unit Properties</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>Duplexes, triplexes, fourplexes</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>Up to 75% LTV</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>DSCR from 1.20</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>Residential loan terms</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h4 className="font-bold text-slate-900 mb-3">5+ Unit Properties</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>Apartment buildings, complexes</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>Up to 80% LTV</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>DSCR from 1.25</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-arrow-right-circle-fill text-blue-600 mr-2 mt-0.5"></i>
                    <span>Commercial loan terms</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              No Ratio DSCR Loans
            </h3>
            
            <p className="text-slate-700 mb-4">
              No ratio DSCR loans are flexible financing options for properties with DSCR below 1.0. These types of DSCR loans are ideal for value-add properties, major renovations, or situations where the property's income will improve after purchase. While they require larger down payments and may have slightly higher rates, no ratio DSCR loans provide financing when traditional DSCR loans won't work.
            </p>

            <p className="text-slate-700 mb-6">
              Investors use no ratio DSCR loans for properties that need rent increases, unit additions, or other improvements that will boost the DSCR above 1.0. Once the property's income improves, borrowers can refinance into a standard DSCR loan with better terms.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Cash-Out Refinance DSCR Loans
            </h3>
            
            <p className="text-slate-700 mb-4">
              Cash-out refinance DSCR loans allow investors to access equity from their rental properties without income verification. These types of DSCR loans are perfect for extracting capital to fund additional property purchases, renovations, or other investments while maintaining your existing rental portfolio.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-6 mb-8">
              <h4 className="font-bold text-slate-900 mb-3">Benefits of DSCR Cash-Out Refinancing:</h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-star-fill text-blue-600 mr-2 mt-1"></i>
                  <span>Access up to 75% of your property's current value</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-blue-600 mr-2 mt-1"></i>
                  <span>No income documentation or tax returns required</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-blue-600 mr-2 mt-1"></i>
                  <span>Use funds for any purpose - more properties, renovations, or debt consolidation</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-star-fill text-blue-600 mr-2 mt-1"></i>
                  <span>Potentially lower your interest rate while extracting equity</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Fix and Flip DSCR Loans
            </h3>
            
            <p className="text-slate-700 mb-4">
              Fix and flip DSCR loans provide short-term financing for property renovations and quick resales. While technically different from traditional DSCR loans, many lenders offer these as part of their DSCR loan product suite. These loans typically feature interest-only payments, 12-24 month terms, and financing up to 90% of purchase price plus renovation costs.
            </p>

            <p className="text-slate-700 mb-6">
              Fix and flip loans are ideal for active real estate investors who buy distressed properties, renovate them, and sell for profit. The short-term nature and interest-only payments minimize holding costs while maximizing returns on successful flips.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Choosing the Right Type of DSCR Loan
            </h3>
            
            <p className="text-slate-700 mb-4">
              Selecting the right type of DSCR loan depends on your investment strategy, property type, and financial goals:
            </p>

            <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <span className="text-blue-600 font-bold">1</span>
                  </div>
                  <div>
                    <strong className="text-slate-900">For traditional buy-and-hold investors:</strong>
                    <p className="text-slate-700 mt-1">Single family rental DSCR loans offer the best combination of rates, terms, and flexibility for building a long-term rental portfolio.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <span className="text-blue-600 font-bold">2</span>
                  </div>
                  <div>
                    <strong className="text-slate-900">For vacation rental investors:</strong>
                    <p className="text-slate-700 mt-1">Short-term rental DSCR loans leverage higher nightly rates to qualify for larger loan amounts in tourist markets.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <span className="text-blue-600 font-bold">3</span>
                  </div>
                  <div>
                    <strong className="text-slate-900">For scaling investors:</strong>
                    <p className="text-slate-700 mt-1">Multifamily DSCR loans provide efficient portfolio growth through properties with multiple income-generating units.</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <span className="text-blue-600 font-bold">4</span>
                  </div>
                  <div>
                    <strong className="text-slate-900">For value-add opportunities:</strong>
                    <p className="text-slate-700 mt-1">No ratio DSCR loans enable purchases of underperforming properties with plans to improve income and refinance later.</p>
                  </div>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Frequently Asked Questions About Types of DSCR Loans
            </h3>

            <div className="space-y-6 mb-8">
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
                <h4 className="font-bold text-slate-900 mb-2">What types of DSCR loans are available?</h4>
                <p className="text-slate-700">
                  The main types of DSCR loans include single family rental loans, short-term rental loans, multifamily loans (2-4 units and 5+ units), no ratio loans, cash-out refinance loans, and fix-and-flip loans. Each type is designed for specific investment strategies and property types.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
                <h4 className="font-bold text-slate-900 mb-2">Can I use DSCR loans for Airbnb properties?</h4>
                <p className="text-slate-700">
                  Yes! Short-term rental DSCR loans are specifically designed for Airbnb, VRBO, and vacation rental properties. These loans use projected short-term rental income based on market data to calculate your DSCR and loan qualification.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
                <h4 className="font-bold text-slate-900 mb-2">What's the difference between single family and multifamily DSCR loans?</h4>
                <p className="text-slate-700">
                  Single family DSCR loans finance one-unit properties, while multifamily DSCR loans finance 2-4 unit or 5+ unit properties. Multifamily loans may require slightly higher DSCR ratios but offer the advantage of multiple income streams from one property.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
                <h4 className="font-bold text-slate-900 mb-2">Can I get a DSCR loan with a ratio below 1.0?</h4>
                <p className="text-slate-700">
                  Yes, no ratio DSCR loans allow DSCR as low as 0.75. These loans require larger down payments (typically 25-30%) and may have slightly higher interest rates, but they provide financing for value-add properties or situations where income will improve.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
                <h4 className="font-bold text-slate-900 mb-2">Which type of DSCR loan has the best rates?</h4>
                <p className="text-slate-700">
                  Single family rental DSCR loans typically offer the best rates, starting at 6.99% for well-qualified borrowers with DSCR of 1.25+. Short-term rental and multifamily loans may have slightly higher rates depending on property type and risk factors.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:border-blue-300 transition-colors">
                <h4 className="font-bold text-slate-900 mb-2">Can I use different types of DSCR loans for different properties?</h4>
                <p className="text-slate-700">
                  Absolutely! Most investors use multiple types of DSCR loans across their portfolio. You might have single family rental loans for traditional properties, short-term rental loans for vacation homes, and multifamily loans for apartment buildings - all without income verification.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-2xl p-8 text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Find the Perfect DSCR Loan Type for Your Investment</h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Connect with specialized DSCR lenders who offer all types of DSCR loans. Get personalized recommendations based on your property type and investment strategy.
              </p>
              <Link
                to="/apply"
                className="inline-block bg-white text-blue-900 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-lg"
              >
                Get Started Today
              </Link>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
